import env from '../config/env';

const ADMIN_AUTH_FLAGS = ['ct_blog_admin_session', 'ct_admin_auth'];
const API_TIMEOUT_MS = 12000;
const READ_CACHE_TTL_MS = 30000;
const inflightGetRequests = new Map();
const getResponseCache = new Map();

function clearCachedEntries(prefix = '') {
  Array.from(getResponseCache.keys()).forEach((key) => {
    if (!prefix || key.startsWith(prefix)) {
      getResponseCache.delete(key);
    }
  });

  Array.from(inflightGetRequests.keys()).forEach((key) => {
    if (!prefix || key.startsWith(prefix)) {
      inflightGetRequests.delete(key);
    }
  });
}

function getLambdaCloudUrl() {
  return env.ADMIN_API_URL || env.COMMENTS_API_URL || env.CALCULATOR_API_URL || '';
}

function getBaseUrl(resource, requiresAdmin = false) {
  if (resource === 'auth' || resource === 'comments' || requiresAdmin) {
    return getLambdaCloudUrl();
  }
  return env.CONTENT_API_URL || '';
}

function buildUrl(query = {}, requiresAdmin = false) {
  const baseUrl = getBaseUrl(query.resource, requiresAdmin);

  if (!baseUrl) {
    throw new Error(
      query.resource === 'auth' || query.resource === 'comments' || requiresAdmin
        ? 'No hay una API configurada para administración. Define REACT_APP_ADMIN_API_URL o REACT_APP_CALCULATOR_API_URL.'
        : 'No hay una API configurada para contenidos. Define REACT_APP_CONTENT_API_URL.'
    );
  }

  const url = new URL(baseUrl);

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, item));
      return;
    }

    url.searchParams.set(key, value);
  });

  return url.toString();
}

function getCachedPayload(cacheKey) {
  const cached = getResponseCache.get(cacheKey);

  if (!cached) {
    return null;
  }

  if (Date.now() - cached.timestamp > READ_CACHE_TTL_MS) {
    getResponseCache.delete(cacheKey);
    return null;
  }

  return cached.payload;
}

function setCachedPayload(cacheKey, payload) {
  getResponseCache.set(cacheKey, {
    payload,
    timestamp: Date.now(),
  });
}

export function setAdminSession() {
  ADMIN_AUTH_FLAGS.forEach((storageKey) => sessionStorage.setItem(storageKey, 'true'));
}

export function clearAdminSession() {
  ADMIN_AUTH_FLAGS.forEach((storageKey) => sessionStorage.removeItem(storageKey));
  sessionStorage.removeItem('ct_admin_api_key');
  sessionStorage.removeItem('ct_admin_api_token');
  clearCachedEntries('admin:');
}

export function hasAdminSession() {
  return ADMIN_AUTH_FLAGS.some((storageKey) => sessionStorage.getItem(storageKey) === 'true');
}

export async function apiRequest({
  resource,
  method = 'GET',
  id,
  slug,
  action,
  query,
  body,
  requiresAdmin = false,
}) {
  const headers = {};
  const url = buildUrl({
    resource,
    id,
    slug,
    action,
    ...query,
  }, requiresAdmin);

  if (body) {
    headers['Content-Type'] = 'application/json';
  }

  if (requiresAdmin) {
    if (!hasAdminSession()) {
      throw new Error('Tu sesion de admin expiro. Inicia sesion de nuevo.');
    }
    const token = sessionStorage.getItem('ct_admin_api_token');
    if (!token) {
      clearAdminSession();
      throw new Error('Tu sesion de admin expiro. Inicia sesion de nuevo.');
    }
    headers['Authorization'] = `Bearer ${token}`;
  }

  const isGetRequest = method === 'GET';
  const cacheKey = isGetRequest && !requiresAdmin ? `public:${url}` : null;

  if (cacheKey) {
    const cachedPayload = getCachedPayload(cacheKey);
    if (cachedPayload) {
      return cachedPayload;
    }

    const inflightRequest = inflightGetRequests.get(cacheKey);
    if (inflightRequest) {
      return inflightRequest;
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  const requestPromise = (async () => {
    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(payload.error || payload.message || 'La solicitud a la API fallo.');
        if (payload.validation) {
          error.validation = payload.validation;
        }
        throw error;
      }

      if (cacheKey) {
        setCachedPayload(cacheKey, payload);
      }

      return payload;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('La solicitud tardó demasiado. Intenta de nuevo en unos segundos.');
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
      if (cacheKey) {
        inflightGetRequests.delete(cacheKey);
      }
    }
  })();

  if (cacheKey) {
    inflightGetRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
}
